import { ArrowRightAlt } from '@mui/icons-material';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { UsersResponse } from 'lib/api/portfolio/types';
import React from 'react';

import TableCard from '../../../designSystem/Card/TableCard';
import SubtitleWithTooltip from '../../../designSystem/SubtitleWithTooltip';
import { LightTooltip } from '../../../designSystem/WhiteTooltip';

type WalletListProps = {
  users: UsersResponse;
  selectedUserId?: number;
  onChangeUser: (userId: number) => void;
  items: { id: number; name: string }[];
  isSource: boolean;
  onToggleItem?: (itemId: number) => void;
  highlightedNewIds?: number[];
  existingTransferredIds?: number[];
  onToggleAll?: () => void;
  destinationUser?: UsersResponse[number];
};

const WalletList: React.FC<WalletListProps> = (props) => {
  const {
    users,
    selectedUserId,
    onChangeUser,
    items,
    isSource,
    onToggleItem,
    highlightedNewIds = [],
    existingTransferredIds = [],
    onToggleAll,
    destinationUser,
  } = props;

  const handleChange = (e: SelectChangeEvent<string>) => {
    const value = Number(e.target.value);
    if (!Number.isNaN(value)) {
      onChangeUser(value);
    }
  };

  const handleToggleClick = (itemId: number) => {
    if (!onToggleItem) return;
    onToggleItem(itemId);
  };

  return (
    <TableCard enabledEffects={false}>
      <SubtitleWithTooltip
        title={isSource ? 'Cartera origen' : 'Cartera destino'}
        tooltipContent={isSource ? 'Cartera origen' : 'Cartera destino'}
      >
        {isSource && onToggleAll && (
          <LightTooltip
            title={
              <Typography padding={1}>
                Mover todos los clientes a la cartera destino
              </Typography>
            }
          >
            <IconButton
              onClick={onToggleAll}
              color="primary"
              size="small"
              disabled={!destinationUser}
              aria-label="Mover todos los clientes a la cartera destino"
              sx={{
                border: (theme) =>
                  !destinationUser
                    ? `1px solid ${theme.palette.action.disabled}`
                    : `1px solid ${theme.palette.primary.main}`,
                borderRadius: '50%',
                '&:disabled': {
                  borderColor: (theme) => theme.palette.action.disabled,
                  opacity: 0.5,
                  cursor: 'not-allowed',
                },
              }}
            >
              <ArrowRightAlt fontSize="small" />
            </IconButton>
          </LightTooltip>
        )}
      </SubtitleWithTooltip>
      <FormControl fullWidth>
        <InputLabel id="user-select-label">Usuario</InputLabel>
        <Select
          labelId="user-select-label"
          id="user-select"
          label="Usuario"
          value={selectedUserId !== undefined ? String(selectedUserId) : ''}
          onChange={handleChange}
          fullWidth
        >
          {users.map((option) => (
            <MenuItem key={option.id} value={String(option.id)}>
              {option.name} ({option.email})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <List
        sx={{ height: 300, mt: 1, overflowY: 'auto' }}
        dense
        component="div"
        role="list"
      >
        {items.map((value) => {
          const isNew = highlightedNewIds.includes(value.id);
          const isExistingTransferred = existingTransferredIds.includes(
            value.id,
          );
          const showStateChip = !isSource && (isNew || isExistingTransferred);
          const chipLabel = isNew
            ? 'Cliente nuevo'
            : isExistingTransferred
              ? 'Cliente ya existente'
              : '';

          return (
            <ListItemButton
              key={value.id}
              role="listitem"
              onClick={() => handleToggleClick(value.id)}
              sx={{
                width: '100%',
                borderRadius: 1,
                mb: 0.5,
                bgcolor: !isSource
                  ? isNew
                    ? 'action.hover'
                    : isExistingTransferred
                      ? 'action.selected'
                      : 'transparent'
                  : 'transparent',
                '&:hover': {
                  bgcolor: !isSource
                    ? isNew
                      ? 'action.selected'
                      : 'action.hover'
                    : 'action.hover',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 1.5,
                }}
              >
                <ListItemText
                  id={String(value.id)}
                  primary={value.name}
                  primaryTypographyProps={{
                    noWrap: true,
                    fontWeight: isNew ? 600 : 400,
                  }}
                />
                {showStateChip && chipLabel && (
                  <Chip
                    label={chipLabel}
                    variant="outlined"
                    size="small"
                    color={isNew ? 'primary' : 'default'}
                    sx={{
                      flexShrink: 0,
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
            </ListItemButton>
          );
        })}
      </List>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 1,
          pr: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {items.length} {items.length === 1 ? 'cliente' : 'clientes'}
        </Typography>
      </Box>
    </TableCard>
  );
};

export default WalletList;
